
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, Activity, Star, Calendar, Tag, Filter, AlertTriangle, Clock, Target, BarChart3, CheckCircle, Lightbulb, Zap, Shield, Eye } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Decision } from "@/hooks/useDecisions";



interface RatingData {
  R0: number;
  RX: number | undefined;
  RY: number | undefined;
  RZ: number | undefined;
}

/** A row of either the tag or the emotion distribution list. */
type DistributionRow = { tag?: string; emotion?: string; count: number };

interface Insight {
  type: 'trajectory' | 'meta';
  severity: 'info' | 'warning' | 'success';
  title: string;
  message: string;
  icon: LucideIcon;
}

// Helper Functions for Rating Analysis
const extractRatings = (decision: Decision): RatingData => {
  const R0 = decision.day0_rating;
  const ratingHistory = decision.rating_history || [];
  
  // Extract ratings based on standard review intervals [7, 30, 90]
  const RX = ratingHistory.find(r => r.day === 7)?.rating;
  const RY = ratingHistory.find(r => r.day === 30)?.rating;
  const RZ = ratingHistory.find(r => r.day === 90)?.rating;
  
  return { R0, RX, RY, RZ };
};

// ============================
// TRAJECTORY INSIGHTS (Rules 1-20)
// ============================

// Rule 1: Long-Term Improvement Pattern
const checkLongTermImprovement = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const improvedCount = validDecisions.filter(d => {
    const { R0, RZ } = extractRatings(d);
    return RZ! - R0 >= 2;
  }).length;
  
  const percentage = improvedCount / validDecisions.length;
  
  if (percentage > 0.5) {
    return {
      type: 'trajectory',
      severity: 'success',
      title: 'Long-Term Improvement',
      message: 'You often feel better about your decisions over time. That suggests you\'re making more solid choices than you might think at first.',
      icon: TrendingUp
    };
  }
  return null;
};

const checkShortTermDoubt = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const doubtCount = validDecisions.filter(d => {
    const { R0, RX, RZ } = extractRatings(d);
    return RX! < R0 && RZ! > R0;
  }).length;
  
  const percentage = doubtCount / validDecisions.length;
  
  if (percentage > 0.4) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Short-Term Doubt, Later Confidence',
      message: 'You tend to doubt decisions early on, but they often improve. Consider giving choices time before reassessing.',
      icon: Clock
    };
  }
  return null;
};

const checkStableJudgment = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const stableCount = validDecisions.filter(d => {
    const { R0, RX, RY, RZ } = extractRatings(d);
    return Math.abs(RZ! - R0) <= 1 && Math.abs(RY! - R0) <= 1 && Math.abs(RX! - R0) <= 1;
  }).length;
  
  const percentage = stableCount / validDecisions.length;
  
  if (percentage > 0.6) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Stable Judgment',
      message: 'Your opinion rarely shifts. That suggests clear expectations and steady evaluation.',
      icon: Target
    };
  }
  return null;
};

const checkDeclineOverTime = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const declinedCount = validDecisions.filter(d => {
    const { R0, RZ } = extractRatings(d);
    return RZ! < R0 - 1;
  }).length;
  
  const percentage = declinedCount / validDecisions.length;
  
  if (percentage > 0.4) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Decline Over Time',
      message: 'Your satisfaction tends to drop over time. Consider what tradeoffs are emerging in hindsight.',
      icon: TrendingDown
    };
  }
  return null;
};

const checkRatingVolatility = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const volatileCount = validDecisions.filter(d => {
    const { R0, RX, RY, RZ } = extractRatings(d);
    const allRatings = [R0, RX!, RY!, RZ!];
    const max = Math.max(...allRatings);
    const min = Math.min(...allRatings);
    return (max - min) >= 4;
  }).length;
  
  const percentage = volatileCount / validDecisions.length;
  
  if (percentage > 0.3) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Rating Volatility',
      message: 'Your ratings swing significantly. You might be navigating complex or shifting situations.',
      icon: BarChart3
    };
  }
  return null;
};

const checkOverconfidenceEarly = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const overconfidentCount = validDecisions.filter(d => {
    const { R0, RZ } = extractRatings(d);
    return R0 >= 8 && RZ! <= 5;
  }).length;
  
  const percentage = overconfidentCount / validDecisions.length;
  
  if (percentage > 0.25) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Overconfidence Early On',
      message: 'You often begin very confident, but satisfaction fades. Consider testing assumptions earlier.',
      icon: AlertTriangle
    };
  }
  return null;
};

// Rule 7: Honeymoon Phase Pattern
const checkHoneymoonPhase = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const honeymoonCount = validDecisions.filter(d => {
    const { R0, RX, RY } = extractRatings(d);
    return RX! > R0 && RY! < RX!;
  }).length;
  
  const percentage = honeymoonCount / validDecisions.length;
  
  if (percentage > 0.4) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Honeymoon Phase Pattern',
      message: 'You often feel best about decisions in the short term, then reality sets in. Consider the full lifecycle.',
      icon: Zap
    };
  }
  return null;
};

// Rule 8: Gradual Satisfaction
const checkGradualSatisfaction = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const gradualCount = validDecisions.filter(d => {
    const { R0, RX, RY, RZ } = extractRatings(d);
    return RX! > R0 && RY! > RX! && RZ! > RY!;
  }).length;
  
  const percentage = gradualCount / validDecisions.length;
  
  if (percentage > 0.3) {
    return {
      type: 'trajectory',
      severity: 'success',
      title: 'Gradual Satisfaction',
      message: 'Your satisfaction builds over time. You make choices that compound positively.',
      icon: TrendingUp
    };
  }
  return null;
};

// Rule 9: U-Shaped Recovery
const checkUShapedRecovery = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const uShapeCount = validDecisions.filter(d => {
    const { R0, RX, RY, RZ } = extractRatings(d);
    return RX! < R0 && RY! < RX! && RZ! > RY! && RZ! >= R0;
  }).length;
  
  const percentage = uShapeCount / validDecisions.length;
  
  if (percentage > 0.25) {
    return {
      type: 'trajectory',
      severity: 'success',
      title: 'U-Shaped Recovery',
      message: 'You navigate through difficult periods and recover well. Your resilience shows in long-term outcomes.',
      icon: Target
    };
  }
  return null;
};

// Rule 10: Persistent Regret
const checkPersistentRegret = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RY !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 3) return null;
  
  const regretCount = validDecisions.filter(d => {
    const { R0, RX, RY, RZ } = extractRatings(d);
    return RX! < R0 && RY! < R0 && RZ! < R0;
  }).length;
  
  const percentage = regretCount / validDecisions.length;
  
  if (percentage > 0.3) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Persistent Regret',
      message: 'Many decisions feel worse over time. Consider what factors you might be overlooking initially.',
      icon: TrendingDown
    };
  }
  return null;
};

// Rule 11: High Initial Standards
const checkHighInitialStandards = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 10) return null;
  
  const avgInitialRating = decisions.reduce((sum, d) => sum + d.day0_rating, 0) / decisions.length;
  
  if (avgInitialRating >= 8) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'High Initial Standards',
      message: 'You set high bars for your decisions. This can be good, but watch for perfectionism that might limit action.',
      icon: Star
    };
  }
  return null;
};

// Rule 12: Conservative Approach
const checkConservativeApproach = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 10) return null;
  
  const avgInitialRating = decisions.reduce((sum, d) => sum + d.day0_rating, 0) / decisions.length;
  
  if (avgInitialRating <= 5) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Conservative Approach',
      message: 'You tend to be cautious about decisions upfront. This might protect you from risks but could limit opportunities.',
      icon: Shield
    };
  }
  return null;
};

// Rule 13: Quick Disillusionment
const checkQuickDisillusionment = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined;
  });
  
  if (validDecisions.length < 5) return null;
  
  const disillusionCount = validDecisions.filter(d => {
    const { R0, RX } = extractRatings(d);
    return R0 >= 7 && RX! <= 4;
  }).length;
  
  const percentage = disillusionCount / validDecisions.length;
  
  if (percentage > 0.25) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Quick Disillusionment',
      message: 'High hopes often meet harsh reality quickly. Consider more thorough upfront analysis.',
      icon: Eye
    };
  }
  return null;
};

// Rule 14: Slow Burn Success
const checkSlowBurnSuccess = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 5) return null;
  
  const slowBurnCount = validDecisions.filter(d => {
    const { R0, RZ } = extractRatings(d);
    return R0 <= 6 && RZ! >= 8;
  }).length;
  
  const percentage = slowBurnCount / validDecisions.length;
  
  if (percentage > 0.25) {
    return {
      type: 'trajectory',
      severity: 'success',
      title: 'Slow Burn Success',
      message: 'You often underestimate your decisions initially, but they pay off well over time. Trust your judgment more.',
      icon: CheckCircle
    };
  }
  return null;
};

// Rule 15-20: Additional Trajectory Patterns
const checkBinaryThinking = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 10) return null;
  
  const extremeRatings = decisions.filter(d => d.day0_rating <= 3 || d.day0_rating >= 8).length;
  const percentage = extremeRatings / decisions.length;
  
  if (percentage > 0.7) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Binary Thinking Pattern',
      message: 'You tend to see decisions as either very good or very bad. Consider the middle ground more often.',
      icon: BarChart3
    };
  }
  return null;
};

const checkConsistentOptimism = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 10) return null;
  
  const highRatings = decisions.filter(d => d.day0_rating >= 7).length;
  const percentage = highRatings / decisions.length;
  
  if (percentage > 0.8) {
    return {
      type: 'trajectory',
      severity: 'success',
      title: 'Consistent Optimism',
      message: 'You maintain a positive outlook on most decisions. This confidence serves you well.',
      icon: Star
    };
  }
  return null;
};

const checkPessimisticTrend = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 10) return null;
  
  const lowRatings = decisions.filter(d => d.day0_rating <= 4).length;
  const percentage = lowRatings / decisions.length;
  
  if (percentage > 0.6) {
    return {
      type: 'trajectory',
      severity: 'warning',
      title: 'Pessimistic Trend',
      message: 'You often rate decisions poorly upfront. Consider what factors might be affecting your initial confidence.',
      icon: TrendingDown
    };
  }
  return null;
};

const checkMoodSwings = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 15) return null;
  
  const sortedDecisions = [...decisions].sort((a, b) => 
    new Date(a.decision_date).getTime() - new Date(b.decision_date).getTime()
  );
  
  let swingCount = 0;
  for (let i = 2; i < sortedDecisions.length; i++) {
    const prev2 = sortedDecisions[i-2].day0_rating;
    const prev1 = sortedDecisions[i-1].day0_rating;
    const current = sortedDecisions[i].day0_rating;
    
    if ((prev1 > prev2 + 2 && current < prev1 - 2) || 
        (prev1 < prev2 - 2 && current > prev1 + 2)) {
      swingCount++;
    }
  }
  
  const percentage = swingCount / (decisions.length - 2);
  
  if (percentage > 0.3) {
    return {
      type: 'trajectory',
      severity: 'info',
      title: 'Mood Swings in Decision Making',
      message: 'Your confidence levels vary significantly between decisions. Consider external factors that might influence your judgment.',
      icon: BarChart3
    };
  }
  return null;
};

const checkDecisionFrequency = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 20) return null;
  
  const now = new Date();
  const last30Days = decisions.filter(d => {
    const daysSince = (now.getTime() - new Date(d.decision_date).getTime()) / (1000 * 60 * 60 * 24);
    return daysSince <= 30;
  }).length;
  
  if (last30Days > 15) {
    return {
      type: 'meta',
      severity: 'info',
      title: 'High Decision Frequency',
      message: 'You\'re making many documented decisions lately. This shows good self-awareness but watch for decision fatigue.',
      icon: Clock
    };
  } else if (last30Days < 2) {
    return {
      type: 'meta',
      severity: 'info',
      title: 'Low Decision Frequency',
      message: 'You haven\'t logged many decisions recently. Consider documenting smaller choices to build the habit.',
      icon: Clock
    };
  }
  return null;
};

const checkReviewConsistency = (decisions: Decision[]): Insight | null => {
  const decisionsWithReviews = decisions.filter(d => d.rating_history.length > 0);
  if (decisionsWithReviews.length < 5) return null;
  
  const reviewRate = decisionsWithReviews.length / decisions.length;
  
  if (reviewRate > 0.8) {
    return {
      type: 'meta',
      severity: 'success',
      title: 'Excellent Review Discipline',
      message: 'You consistently follow up on your decisions. This discipline will pay off in better decision-making over time.',
      icon: CheckCircle
    };
  } else if (reviewRate < 0.3) {
    return {
      type: 'meta',
      severity: 'warning',
      title: 'Inconsistent Reviews',
      message: 'You rarely follow up on decisions. Regular reviews help identify what works and what doesn\'t.',
      icon: AlertTriangle
    };
  }
  return null;
};

// ============================
// META INSIGHTS (Rules 21-30)
// ============================

const checkRisingInitialRatings = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 20) return null;
  
  const sortedDecisions = [...decisions].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  
  const lastTen = sortedDecisions.slice(-10);
  const previousTen = sortedDecisions.slice(-20, -10);
  
  const avgLast = lastTen.reduce((sum, d) => sum + d.day0_rating, 0) / lastTen.length;
  const avgPrevious = previousTen.reduce((sum, d) => sum + d.day0_rating, 0) / previousTen.length;
  
  if (avgLast > avgPrevious + 1) {
    return {
      type: 'meta',
      severity: 'success',
      title: 'Rising Initial Ratings',
      message: 'You\'re rating decisions more highly upfront. Maybe you\'re becoming more confident or optimistic.',
      icon: TrendingUp
    };
  }
  return null;
};

const checkDecliningInitialRatings = (decisions: Decision[]): Insight | null => {
  if (decisions.length < 20) return null;
  
  const sortedDecisions = [...decisions].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  
  const lastTen = sortedDecisions.slice(-10);
  const previousTen = sortedDecisions.slice(-20, -10);
  
  const avgLast = lastTen.reduce((sum, d) => sum + d.day0_rating, 0) / lastTen.length;
  const avgPrevious = previousTen.reduce((sum, d) => sum + d.day0_rating, 0) / previousTen.length;
  
  if (avgLast < avgPrevious - 1) {
    return {
      type: 'meta',
      severity: 'warning',
      title: 'Declining Initial Ratings',
      message: 'You\'ve become more cautious or skeptical lately. It might reflect stress, doubt, or increased scrutiny.',
      icon: TrendingDown
    };
  }
  return null;
};

const checkShortTermBias = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 5) return null;
  
  const avgRX = validDecisions.reduce((sum, d) => sum + extractRatings(d).RX!, 0) / validDecisions.length;
  const avgRZ = validDecisions.reduce((sum, d) => sum + extractRatings(d).RZ!, 0) / validDecisions.length;
  
  if (avgRX > avgRZ + 1) {
    return {
      type: 'meta',
      severity: 'info',
      title: 'Short-Term Bias',
      message: 'You tend to rate decisions higher in the short term than the long term. Watch for early overconfidence.',
      icon: Clock
    };
  }
  return null;
};

const checkLongTermBias = (decisions: Decision[]): Insight | null => {
  const validDecisions = decisions.filter(d => {
    const ratings = extractRatings(d);
    return ratings.RX !== undefined && ratings.RZ !== undefined;
  });
  
  if (validDecisions.length < 5) return null;
  
  const avgRX = validDecisions.reduce((sum, d) => sum + extractRatings(d).RX!, 0) / validDecisions.length;
  const avgRZ = validDecisions.reduce((sum, d) => sum + extractRatings(d).RZ!, 0) / validDecisions.length;
  
  if (avgRZ > avgRX + 1) {
    return {
      type: 'meta',
      severity: 'success',
      title: 'Long-Term Bias',
      message: 'You tend to rate decisions higher later. You might want to trust your instincts more from the start.',
      icon: CheckCircle
    };
  }
  return null;
};

// Main function to calculate all insights
const calculateAdvancedRecommendations = (decisions: Decision[]): Insight[] => {
  if (decisions.length < 5) {
    return [{
      type: 'meta',
      severity: 'info',
      title: 'Insufficient Data',
      message: 'You haven\'t entered enough decisions to draw insights from yet, keep documenting your decisions and let\'s revisit',
      icon: Lightbulb
    }];
  }
  
  const insights: Insight[] = [];
  
  // Rating Trajectory Insights (Rules 1-20)
  const trajectoryChecks = [
    checkLongTermImprovement,
    checkShortTermDoubt,
    checkStableJudgment,
    checkDeclineOverTime,
    checkRatingVolatility,
    checkOverconfidenceEarly,
    checkHoneymoonPhase,
    checkGradualSatisfaction,
    checkUShapedRecovery,
    checkPersistentRegret,
    checkHighInitialStandards,
    checkConservativeApproach,
    checkQuickDisillusionment,
    checkSlowBurnSuccess,
    checkBinaryThinking,
    checkConsistentOptimism,
    checkPessimisticTrend,
    checkMoodSwings
  ];
  
  // Meta Insights (Rules 21-30)
  const metaChecks = [
    checkDecisionFrequency,
    checkReviewConsistency,
    checkRisingInitialRatings,
    checkDecliningInitialRatings,
    checkShortTermBias,
    checkLongTermBias
  ];
  
  // Execute all checks and collect insights
  [...trajectoryChecks, ...metaChecks].forEach(check => {
    const insight = check(decisions);
    if (insight) insights.push(insight);
  });
  
  // Sort insights by severity (warnings first, then info, then success)
  insights.sort((a, b) => {
    const severityOrder = { 'warning': 0, 'info': 1, 'success': 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
  
  // If no patterns detected, show fallback message
  if (insights.length === 0) {
    insights.push({
      type: 'meta',
      severity: 'info',
      title: 'No Clear Patterns Yet',
      message: 'We haven\'t been able to identify meaningful patterns in your decision log yet, but feel free to download all your data in the activity log section and analyze yourself. Let\'s revisit later.',
      icon: BarChart3
    });
  }
  
  return insights;
};

export function Analytics({ decisions, defaultTab = "trends" }: { decisions: Decision[], defaultTab?: "trends" | "distribution" | "insights" }) {
  const [selectedTag, setSelectedTag] = useState<string>("all");
  const [timePeriod, setTimePeriod] = useState<string>("all");
  const [distributionMetric, setDistributionMetric] = useState<'tags' | 'emotions'>('tags');

  console.log('Analytics: decisions received:', decisions, decisions.length);

  // Early return if no decisions to render something meaningful
  if (!decisions || decisions.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1>Analytics</h1>
          <p className="text-muted-foreground">
            Insights and trends from your decision journal
          </p>
        </div>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No decisions found. Create your first decision to see analytics.</p>
        </div>
      </div>
    );
  }

  // Get all unique review intervals across all decisions
  const allIntervals = Array.from(new Set(decisions.flatMap(d => d.review_intervals))).sort((a, b) => a - b);
  
  // Get most common intervals (first 3) for the main cards
  const primaryIntervals = allIntervals.slice(0, 3);

  // Get all unique tags
  const allTags = Array.from(new Set(decisions.flatMap(d => d.tags.filter(t => t && t.name).map(t => t.name)))).sort();

  // Filter decisions based on selected filters
  const filteredDecisions = decisions.filter(decision => {
    const tagMatch = selectedTag === "all" || decision.tags.filter(tag => tag && tag.name).some(tag => tag.name === selectedTag);
    
    let timeMatch = true;
    if (timePeriod !== "all") {
      const decisionDate = new Date(decision.decision_date);
      const now = new Date();
      const daysAgo = Math.floor((now.getTime() - decisionDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (timePeriod === "30") timeMatch = daysAgo <= 30;
      else if (timePeriod === "90") timeMatch = daysAgo <= 90;
      else if (timePeriod === "365") timeMatch = daysAgo <= 365;
    }
    
    return tagMatch && timeMatch;
  });

  // Helper function to get rating at specific day
  const getRatingAtDay = (decision: Decision, targetDay: number): number | null => {
    // Find exact match first
    const exactMatch = decision.rating_history.find(r => r.day === targetDay);
    if (exactMatch) return exactMatch.rating;
    
    // If no exact match and it's day 0, return initial rating
    if (targetDay === 0) return decision.day0_rating;
    
    // Return null if no rating exists for this day
    return null;
  };

  // Calculate average ratings at dynamic intervals
  const getAverageRatingAtDay = (day: number): number => {
    const ratingsAtDay = filteredDecisions
      .map(d => getRatingAtDay(d, day))
      .filter(rating => rating !== null);
    
    if (ratingsAtDay.length === 0) return 0;
    return Math.round((ratingsAtDay.reduce((sum, rating) => sum + rating!, 0) / ratingsAtDay.length) * 10) / 10;
  };

  // Get count of ratings at specific day
  const getRatingCountAtDay = (day: number): number => {
    const ratingsAtDay = filteredDecisions
      .map(d => getRatingAtDay(d, day))
      .filter(rating => rating !== null);
    
    return ratingsAtDay.length;
  };

  // Tag analysis calculations with dynamic intervals
  const tagAnalysis = allTags.map(tag => {
    const tagDecisions = decisions.filter(d => d.tags.filter(t => t && t.name).some(t => t.name === tag));
    if (tagDecisions.length === 0) return null;

    // Use the decision's actual review intervals for analysis
    const intervalRatings = primaryIntervals.map(interval => {
      const ratingsForInterval = tagDecisions.map(d => getRatingAtDay(d, interval)).filter(r => r !== null);
      return ratingsForInterval.length > 0 ? ratingsForInterval.reduce((sum, r) => sum + r!, 0) / ratingsForInterval.length : null;
    });

    // Calculate rating change from first to last available interval
    let ratingChange = null;
    const validRatings = intervalRatings.filter(r => r !== null);
    if (validRatings.length >= 2) {
      ratingChange = validRatings[validRatings.length - 1]! - validRatings[0]!;
    }

    // Calculate volatility (standard deviation of interval ratings)
    let volatility = null;
    if (validRatings.length >= 2) {
      const mean = validRatings.reduce((sum, r) => sum + r!, 0) / validRatings.length;
      const variance = validRatings.reduce((sum, r) => sum + Math.pow(r! - mean, 2), 0) / validRatings.length;
      volatility = Math.sqrt(variance);
    }

    return {
      tag,
      intervalRatings,
      ratingChange,
      volatility,
      decisionCount: tagDecisions.length
    };
  }).filter(item => item !== null);

  // Find best and worst performing tags
  const tagsByChange = tagAnalysis
    .filter(t => t!.ratingChange !== null)
    .sort((a, b) => b!.ratingChange! - a!.ratingChange!);

  const bestPerformingTag = tagsByChange[0];
  const worstPerformingTag = tagsByChange[tagsByChange.length - 1];

  // Find most volatile tag
  const mostVolatileTag = tagAnalysis
    .filter(t => t!.volatility !== null)
    .sort((a, b) => b!.volatility! - a!.volatility!)[0];

  // Calculate analytics data
  const totalDecisions = filteredDecisions.length;
  
  // Calculate average initial rating - only include valid ratings
  const validInitialRatings = filteredDecisions
    .map(d => d.day0_rating)
    .filter(rating => rating !== null && rating !== undefined && !isNaN(rating));
  
  console.log('Analytics: valid day0_ratings:', validInitialRatings);
  console.log('Analytics: sum:', validInitialRatings.reduce((sum, rating) => sum + rating, 0));
  console.log('Analytics: count:', validInitialRatings.length);
  
  const avgRating = validInitialRatings.length > 0 
    ? Math.round((validInitialRatings.reduce((sum, rating) => sum + rating, 0) / validInitialRatings.length) * 10) / 10
    : 0;
  
  const completedReviews = filteredDecisions.reduce((sum, d) => 
    sum + d.scheduled_reviews.filter((r) => r.status === "completed").length, 0
  );
  
  const totalReviews = filteredDecisions.reduce((sum, d) => sum + d.scheduled_reviews.length, 0);
  const reviewCompletionRate = totalReviews > 0 ? Math.round((completedReviews / totalReviews) * 100) : 0;

  // Monthly decision trend
  const monthlyData = filteredDecisions.reduce<Record<string, { count: number; date: Date }>>((acc, decision) => {
    const date = new Date(decision.decision_date);
    const month = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
    if (!acc[month]) {
      acc[month] = {
        count: 0,
        date: date // Store the actual date for sorting
      };
    }
    acc[month].count += 1;
    return acc;
  }, {});

  const monthlyDecisions = Object.entries(monthlyData)
    .map(([month, data]) => ({
      month,
      decisions: data.count,
      sortDate: data.date
    }))
    .sort((a, b) => new Date(a.sortDate).getTime() - new Date(b.sortDate).getTime())
    .map(({ month, decisions }) => ({ month, decisions }));

  // Rating distribution
  const ratingData = Array.from({ length: 10 }, (_, i) => {
    const rating = i + 1;
    const count = filteredDecisions.filter(d => d.day0_rating === rating).length;
    return { rating: `${rating}`, count };
  });

  // Tag distribution
  const tagCounts = filteredDecisions.reduce<Record<string, number>>((acc, decision) => {
    decision.tags.filter(tag => tag && tag.name).forEach((tag) => {
      acc[tag.name] = (acc[tag.name] || 0) + 1;
    });
    return acc;
  }, {});

  const tagData = Object.entries(tagCounts)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 5)
    .map(([tag, count]) => ({ tag, count }));

  // Emotion distribution
  const emotionCounts = filteredDecisions.reduce<Record<string, number>>((acc, decision) => {
    if (decision.emotions && Array.isArray(decision.emotions)) {
      decision.emotions.filter(emotion => emotion && emotion.name).forEach((emotion) => {
        const emotionName = emotion.name.trim().toLowerCase();
        acc[emotionName] = (acc[emotionName] || 0) + 1;
      });
    }
    return acc;
  }, {});

  const emotionData = Object.entries(emotionCounts)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 8)
    .map(([emotion, count]) => ({ 
      emotion: emotion.charAt(0).toUpperCase() + emotion.slice(1), 
      count 
    }));

  // Monthly confidence trend - trailing 12 months
  const confidenceData = (() => {
    const now = new Date();
    const months: Array<{
      month: string;
      date: string;
      ratings: number[];
    }> = [];
    
    // Generate last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`;
      months.push({
        month: monthKey,
        date: date.toISOString().slice(0, 7), // YYYY-MM format for comparison
        ratings: []
      });
    }
    
    // Group decisions by month
    filteredDecisions.forEach(decision => {
      const decisionMonth = decision.decision_date.slice(0, 7); // YYYY-MM format
      const monthData = months.find(m => m.date === decisionMonth);
      if (monthData) {
        monthData.ratings.push(decision.day0_rating);
      }
    });
    
    // Calculate monthly averages
    return months.map(monthData => ({
      month: monthData.month,
      rating: monthData.ratings.length > 0 
        ? Math.round((monthData.ratings.reduce((sum, rating) => sum + rating, 0) / monthData.ratings.length) * 10) / 10
        : null,
      count: monthData.ratings.length
    })).filter(data => data.rating !== null); // Only show months with data
  })();

  // Top emotions (reuse emotionCounts from above)
  const topEmotions = Object.entries(emotionCounts)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 5);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="mb-6">
          <h1>Analytics</h1>
          <p className="text-muted-foreground">
            Insights and trends from your decision journal
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
            <CardDescription>Filter analytics by tag and time period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tag</label>
                <Select value={selectedTag} onValueChange={setSelectedTag}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {allTags.map(tag => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Time Period</label>
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSelectedTag("all");
                    setTimePeriod("all");
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Decisions</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalDecisions}</div>
              <p className="text-xs text-muted-foreground">
                Decisions recorded
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Initial Rating</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{avgRating}/10</div>
              <p className="text-xs text-muted-foreground">
                Initial confidence
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Review Rate</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reviewCompletionRate}%</div>
              <p className="text-xs text-muted-foreground">
                {completedReviews}/{totalReviews} completed
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique Tags</CardTitle>
              <Tag className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(tagCounts).length}</div>
              <p className="text-xs text-muted-foreground">
                Categories used
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Dynamic Review Interval Cards */}
        {primaryIntervals.length > 0 && (
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            {primaryIntervals.map((interval, index) => (
              <Card key={interval}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Rating at Day {interval}</CardTitle>
                  <Star className={`h-4 w-4 ${index === 0 ? 'text-blue-500' : index === 1 ? 'text-green-500' : 'text-purple-500'}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{getAverageRatingAtDay(interval)}/10</div>
                  <p className="text-xs text-muted-foreground">
                    Number of Ratings: {getRatingCountAtDay(interval)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tag Analysis Cards */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          {bestPerformingTag && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Best Improving Tag</CardTitle>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {bestPerformingTag.tag}
                </div>
                <p className="text-xs text-muted-foreground">
                  +{bestPerformingTag.ratingChange!.toFixed(1)} rating improvement
                </p>
              </CardContent>
            </Card>
          )}

          {worstPerformingTag && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Most Declining Tag</CardTitle>
                <TrendingDown className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {worstPerformingTag.tag}
                </div>
                <p className="text-xs text-muted-foreground">
                  {worstPerformingTag.ratingChange!.toFixed(1)} rating change
                </p>
              </CardContent>
            </Card>
          )}

          {mostVolatileTag && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Most Volatile Tag</CardTitle>
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {mostVolatileTag.tag}
                </div>
                <p className="text-xs text-muted-foreground">
                  {mostVolatileTag.volatility!.toFixed(1)} volatility score
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="distribution">Distribution</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="trends" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Decisions Over Time</CardTitle>
                  <CardDescription>Monthly decision-making activity</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyDecisions} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="decisions" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Confidence Trend</CardTitle>
                  <CardDescription>Monthly average confidence ratings (trailing 12 months)</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={confidenceData} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        interval={0}
                      />
                      <YAxis domain={[0, 10]} allowDecimals={false} />
                      <Tooltip 
                        labelFormatter={(value) => value}
                        formatter={(value: unknown, _name: unknown, props: unknown) => [
                          `${value} (${(props as { payload: { count: number } }).payload.count} decisions)`,
                          'Avg Rating'
                        ]}
                      />
                      <Line type="monotone" dataKey="rating" stroke="#8884d8" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="distribution" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Rating Distribution</CardTitle>
                  <CardDescription>How confident you are in your decisions</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ratingData} margin={{ top: 20, right: 20, left: 20, bottom: 50 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="rating" 
                        label={{ value: 'Rating (1-10)', position: 'insideBottom', offset: -5 }}
                        type="category"
                      />
                      <YAxis 
                        allowDecimals={false}
                        type="number"
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill="#00C49F" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Top Decision Categories</CardTitle>
                      <CardDescription>Analysis by {distributionMetric === 'tags' ? 'most frequently used tags' : 'emotional patterns'}</CardDescription>
                    </div>
                    <Select 
                      value={distributionMetric} 
                      onValueChange={(value: 'tags' | 'emotions') => setDistributionMetric(value)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tags">Tags</SelectItem>
                        <SelectItem value="emotions">Emotions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(distributionMetric === 'tags' ? tagData : emotionData)
                      .slice(0, 10)
                      .map((item: DistributionRow, index: number) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-muted-foreground w-6">
                              #{index + 1}
                            </span>
                            <span className="text-sm font-medium">
                              {distributionMetric === 'tags' ? item.tag : item.emotion}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div 
                              className="h-2 bg-primary rounded-full" 
                              style={{ 
                                width: `${Math.max(20, (Number(item.count) / Math.max(...(distributionMetric === 'tags' ? tagData : emotionData).map((d: DistributionRow) => Number(d.count)))) * 60)}px` 
                              }}
                            />
                            <span className="text-sm font-semibold min-w-[2rem] text-right">
                              {Number(item.count)}
                            </span>
                          </div>
                        </div>
                      ))
                    }
                    {(distributionMetric === 'tags' ? tagData : emotionData).length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        No {distributionMetric} data available
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Decision Quality</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">High (8-10)</span>
                       <Badge variant="secondary">
                         {filteredDecisions.filter(d => d.day0_rating >= 8).length} decisions
                       </Badge>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-sm">Medium (5-7)</span>
                        <Badge variant="secondary">
                          {filteredDecisions.filter(d => d.day0_rating >= 5 && d.day0_rating < 8).length} decisions
                        </Badge>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-sm">Low (1-4)</span>
                       <Badge variant="secondary">
                         {filteredDecisions.filter(d => d.day0_rating < 5).length} decisions
                       </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Emotions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topEmotions.map(([emotion, count]) => (
                      <div key={emotion} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{emotion}</span>
                        <Badge variant="secondary">{count as number}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Review Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Completed Reviews</span>
                      <Badge variant="default">{completedReviews}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Pending Reviews</span>
                       <Badge variant="destructive">
                         {filteredDecisions.reduce((sum, d) => 
                           sum + d.scheduled_reviews.filter((r) => r.status === "pending").length, 0
                         )}
                       </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Completion Rate</span>
                      <Badge variant="secondary">{reviewCompletionRate}%</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Recommendations</span>
                  <Badge variant="secondary" className="text-xs">
                    {calculateAdvancedRecommendations(decisions).length} insights
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Based on your decision-making patterns - analyzing {totalDecisions} decisions across {18 + 6} rules
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(() => {
                    const recommendations = calculateAdvancedRecommendations(decisions);
                    console.log('Analytics: Generated recommendations:', recommendations.length, 'insights');
                    
                    return recommendations.map((insight, index) => {
                      const Icon = insight.icon;
                      const bgColor = insight.severity === 'success' ? 'bg-green-50 dark:bg-green-950/20' : 
                                     insight.severity === 'warning' ? 'bg-red-50 dark:bg-red-950/20' : 
                                     'bg-blue-50 dark:bg-blue-950/20';
                      const iconColor = insight.severity === 'success' ? 'text-green-600 dark:text-green-400' : 
                                       insight.severity === 'warning' ? 'text-red-600 dark:text-red-400' : 
                                       'text-blue-600 dark:text-blue-400';
                      const textColor = insight.severity === 'success' ? 'text-green-800 dark:text-green-200' : 
                                       insight.severity === 'warning' ? 'text-red-800 dark:text-red-200' : 
                                       'text-blue-800 dark:text-blue-200';
                      const messageColor = insight.severity === 'success' ? 'text-green-700 dark:text-green-300' : 
                                          insight.severity === 'warning' ? 'text-red-700 dark:text-red-300' : 
                                          'text-blue-700 dark:text-blue-300';
                      
                      return (
                        <div key={index} className={`flex items-start gap-3 p-4 ${bgColor} rounded-lg border border-transparent transition-all hover:border-border`}>
                          <div className={`p-1.5 rounded-full ${bgColor}`}>
                            <Icon className={`h-4 w-4 ${iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className={`font-medium ${textColor} leading-tight`}>{insight.title}</p>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Badge 
                                  variant={insight.severity === 'warning' ? 'destructive' : insight.severity === 'success' ? 'default' : 'secondary'} 
                                  className="text-xs"
                                >
                                  {insight.severity === 'warning' ? 'Action Needed' : insight.severity === 'success' ? 'Strength' : 'Info'}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {insight.type === 'meta' ? 'Meta' : 'Pattern'}
                                </Badge>
                              </div>
                            </div>
                            <p className={`text-sm ${messageColor} leading-relaxed`}>
                              {insight.message}
                            </p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {calculateAdvancedRecommendations(decisions).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No insights generated yet. Keep logging decisions to unlock patterns!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </div>
  );
}
